export const getCachedLocation = () => {
  const cachedLocation = localStorage.getItem('userLocation');
  const cachedCity = localStorage.getItem('userCity');
  const cachedState = localStorage.getItem('userState');

  if (cachedLocation && cachedCity) {
    return {
      location: JSON.parse(cachedLocation),
      city: cachedCity,
      state: cachedState || ''
    };
  }
  return null;
};

export const cacheLocation = (location, city, state) => {
  localStorage.setItem('userLocation', JSON.stringify(location));
  localStorage.setItem('userCity', city);
  localStorage.setItem('userState', state || '');
};

// Function to get approximate city name based on coordinates without using Google Maps API
export const getApproximateLocationName = async (lat, lng) => {
  try {
    // Try using a free reverse geocoding API as fallback
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`);
    const data = await response.json();
    
    if (data && data.address) {
      const city = data.address.city || data.address.town || data.address.village || data.address.county || 'Unknown location';
      const state = data.address.state || '';
      return { city, state };
    }
    return { city: 'Unknown location', state: '' };
  } catch (error) {
    console.error('Error with fallback geocoding:', error);
    return { city: 'Unknown location', state: '' };
  }
};

export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const newLocation = { lat: latitude, lng: longitude };
        
        try {
          // First try with Google Maps API
          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();
            
            if (data.status === "REQUEST_DENIED") {
              console.warn("Google Maps API request denied:", data.error_message);
              // Fall back to alternative geocoding
              const { city, state } = await getApproximateLocationName(latitude, longitude);
              resolve({ location: newLocation, city, state });
              return;
            }
            
            if (!data.results || data.results.length === 0) {
              console.warn("No location results found for coordinates:", latitude, longitude);
              // Fall back to alternative geocoding
              const { city, state } = await getApproximateLocationName(latitude, longitude);
              resolve({ location: newLocation, city, state });
              return;
            }
            
            const addressComponents = data.results[0].address_components;
            let city = "", state = "";
            
            for (const component of addressComponents) {
              if (component.types.includes("locality")) city = component.long_name;
              if (component.types.includes("administrative_area_level_1")) state = component.long_name;
            }
            
            // If we couldn't find a city name, try to use a broader locality
            if (!city) {
              for (const component of addressComponents) {
                if (component.types.includes("sublocality") || 
                    component.types.includes("administrative_area_level_2")) {
                  city = component.long_name;
                  break;
                }
              }
            }
            
            // If we still don't have a city, use a fallback
            if (!city) {
              const { city: fallbackCity, state: fallbackState } = await getApproximateLocationName(latitude, longitude);
              city = fallbackCity;
              if (!state) state = fallbackState;
            }
            
            resolve({ location: newLocation, city, state });
          } catch (googleError) {
            console.warn("Google Maps API error:", googleError);
            // Fall back to alternative geocoding
            const { city, state } = await getApproximateLocationName(latitude, longitude);
            resolve({ location: newLocation, city, state });
          }
        } catch (error) {
          console.error("Error fetching location data:", error);
          // Last resort fallback - just use coordinates
          resolve({ 
            location: newLocation, 
            city: `Location (${latitude.toFixed(2)}, ${longitude.toFixed(2)})`, 
            state: "" 
          });
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        const cachedData = getCachedLocation();
        if (cachedData) {
          resolve(cachedData);
        } else {
          reject(error);
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });
};
